import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { api } from '../api';
import { useToast } from '../components/Toast';
import { getStoredUser } from '../utils/safeStorage';

const PackageDetailPage = ({ navigate, packageData, bookingData }) => {
  const toast = useToast();
  const [pkg, setPkg] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [rooms, setRooms] = useState([{ id: 1, sharing_type: '', num_adults: 0, num_child_no_bed: 0, num_infants: 0 }]);
  const [shareOpen, setShareOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Define max adults per room type
  const maxAdultsPerType = {
    'single': 1,
    'double': 2,
    'triple': 3,
    'quad': 4
  };

  // Calculate price for a single room
  const calculateRoomPrice = (room) => {
    if (!room.sharing_type || !pkg) return 0;
    
    const roomPrice = pkg.room_prices.find(rp => rp.sharing_type === room.sharing_type);
    if (!roomPrice) return 0;
    
    // Get the base price per person (room price is per person)
    const pricePerPerson = parseFloat(roomPrice.price);
    
    // Calculate prices
    const adultPrice = pricePerPerson * room.num_adults;
    const childNoBedPrice = (pricePerPerson * (pkg.child_no_bed_price_percentage / 100)) * room.num_child_no_bed;
    const infantPrice = (pricePerPerson * (pkg.infant_price_percentage / 100)) * room.num_infants;
    
    return adultPrice + childNoBedPrice + infantPrice;
  };

  // Calculate total price for all rooms
  const calculateTotalPrice = () => {
    return rooms.reduce((total, room) => total + calculateRoomPrice(room), 0);
  };

  useEffect(() => {
    if (packageData?.slug) {
      loadPackageDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageData]);

  const loadPackageDetails = async () => {
    setLoadError('');
    try {
      const data = await api.getPackageBySlug(packageData.slug);
      setPkg(data);
    } catch (error) {
      console.error('Error loading package:', error);
      setLoadError(error.message || 'Unable to load this package.');
    }
  };

  const addRoom = () => {
    setRooms([...rooms, { id: rooms.length + 1, sharing_type: '', num_adults: 0, num_child_no_bed: 0, num_infants: 0 }]);
  };

  const removeRoom = (id) => {
    if (rooms.length > 1) {
      setRooms(rooms.filter(room => room.id !== id));
    }
  };

  const updateRoom = (id, field, value) => {
    setRooms(rooms.map(room => {
      if (room.id === id) {
        const updatedRoom = { ...room, [field]: value };
        
        // If sharing type changed, reset adults to 0
        if (field === 'sharing_type') {
          updatedRoom.num_adults = 0;
        }
        
        // Enforce max adults based on room type
        if (field === 'num_adults' && updatedRoom.sharing_type) {
          const maxAdults = maxAdultsPerType[updatedRoom.sharing_type] || 4;
          if (value > maxAdults) {
            alert(`${updatedRoom.sharing_type} room can only have maximum ${maxAdults} adult(s)`);
            updatedRoom.num_adults = maxAdults;
          }
        }
        
        return updatedRoom;
      }
      return room;
    }));
  };

  const handleNext = () => {
    // Check if user is logged in
    const savedUser = getStoredUser();
    if (!savedUser) {
      toast.info('Please login to continue booking');
      navigate('login');
      return;
    }

    const validRooms = rooms.filter(r => r.sharing_type && (r.num_adults > 0 || r.num_child_no_bed > 0 || r.num_infants > 0));
    
    if (validRooms.length === 0) {
      toast.error('Please select at least one room with passengers');
      return;
    }
    
    const roomsWithNumbers = validRooms.map((r, i) => ({ ...r, room_number: i + 1 }));
    console.log('Rooms being sent to next page:', roomsWithNumbers);
    
    navigate('passengers', { bookingData: { ...bookingData, rooms: roomsWithNumbers } });
  };

  if (!pkg) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar navigate={navigate} />
        <div className="pt-28 text-center py-20 px-4">
          {loadError ? (
            <>
              <p className="text-red-600 mb-4">{loadError}</p>
              <button onClick={loadPackageDetails} className="bg-primary text-white px-6 py-2 rounded-lg font-semibold">Retry</button>
            </>
          ) : (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
              <p className="mt-4 text-slate-500">Loading package…</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar navigate={navigate} />
      
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24 md:pt-28">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {pkg.featured_image && (
              <img src={pkg.featured_image} alt={pkg.name} className="w-full h-52 sm:h-72 md:h-96 object-cover rounded-lg mb-6" />
            )}
            
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <h1 className="text-2xl sm:text-4xl font-bold flex-1">{pkg.name}</h1>

              {/* ── Share Button ── */}
              <div className="relative shrink-0">
                <button
                  onClick={() => setShareOpen(p => !p)}
                  className="flex items-center gap-2 border border-sand hover:border-accent text-ink/70 hover:text-primary bg-white px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Share
                </button>

                {shareOpen && (
                  <>
                    {/* backdrop */}
                    <div className="fixed inset-0 z-10" onClick={() => setShareOpen(false)} />
                    <div className="absolute right-0 top-11 z-20 bg-white rounded-xl shadow-2xl border border-sand w-64 overflow-hidden">
                      <div className="px-4 py-3 border-b border-sand">
                        <p className="text-xs font-semibold text-ink/50 uppercase tracking-wider">Share this package</p>
                      </div>

                      {/* Copy Link */}
                      <button
                        onClick={() => {
                          const url = `${window.location.origin}/?package=${pkg.slug}`;
                          navigator.clipboard.writeText(url).then(() => {
                            setLinkCopied(true);
                            toast.success('Link copied to clipboard!');
                            setTimeout(() => setLinkCopied(false), 3000);
                          }).catch(() => {
                            // fallback for older browsers
                            const ta = document.createElement('textarea');
                            ta.value = `${window.location.origin}/?package=${pkg.slug}`;
                            document.body.appendChild(ta);
                            ta.select();
                            document.execCommand('copy');
                            document.body.removeChild(ta);
                            setLinkCopied(true);
                            toast.success('Link copied!');
                            setTimeout(() => setLinkCopied(false), 3000);
                          });
                          setShareOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-cream transition-colors text-left"
                      >
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${linkCopied ? 'bg-green-100' : 'bg-sand'}`}>
                          {linkCopied
                            ? <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            : <svg className="w-4 h-4 text-ink/60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                          }
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-ink">{linkCopied ? 'Copied!' : 'Copy Link'}</p>
                          <p className="text-xs text-ink/40 truncate max-w-[160px]">{window.location.origin}/?package={pkg.slug}</p>
                        </div>
                      </button>

                      {/* WhatsApp */}
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(`Check out this Umrah package: ${pkg.name}\n${window.location.origin}/?package=${pkg.slug}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setShareOpen(false)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-cream transition-colors"
                      >
                        <span className="w-8 h-8 rounded-full bg-[#25D366]/15 flex items-center justify-center shrink-0">
                          <svg className="w-4 h-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.5 3.5A11 11 0 006.3 18.7L3 21l2.4-3.2A11 11 0 1020.5 3.5zm-8.5 16a9 9 0 01-4.6-1.3l-.3-.2-2.7.9.9-2.6-.2-.3A9 9 0 1112 19.5zm5-6.7c-.3-.1-1.6-.8-1.9-.9s-.4-.1-.6.1-.7.9-.8 1-.3.2-.6.1a7.4 7.4 0 01-2.2-1.4 8.2 8.2 0 01-1.5-1.9c-.2-.3 0-.4.1-.6l.4-.5.1-.3a.5.5 0 000-.5c0-.1-.6-1.4-.8-1.9s-.4-.4-.6-.4h-.5a1 1 0 00-.7.3 2.9 2.9 0 00-.9 2.2 5 5 0 001.1 2.6 11.5 11.5 0 004.4 3.9c1.6.7 2.2.8 3 .7a2.5 2.5 0 001.7-1.2 2 2 0 00.1-1.2c-.1-.2-.3-.2-.6-.3z" />
                          </svg>
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-ink">WhatsApp</p>
                          <p className="text-xs text-ink/40">Send via WhatsApp</p>
                        </div>
                      </a>

                      {/* Email */}
                      <a
                        href={`mailto:?subject=${encodeURIComponent(`Umrah Package: ${pkg.name}`)}&body=${encodeURIComponent(`Assalamu Alaikum,\n\nI wanted to share this Umrah package with you:\n\n${pkg.name}\nFrom S$${pkg.room_prices?.[0]?.price || ''} per person\nTravel Date: ${new Date(pkg.travel_date).toLocaleDateString()}\n\nView package: ${window.location.origin}/?package=${pkg.slug}`)}`}
                        onClick={() => setShareOpen(false)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-cream transition-colors border-t border-sand"
                      >
                        <span className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-ink">Email</p>
                          <p className="text-xs text-ink/40">Send via email</p>
                        </div>
                      </a>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-6 text-sm">
              <span className="bg-cream text-primary px-3 py-1 rounded">{pkg.category.name}</span>
              <span className="text-ink/65">📅 {new Date(pkg.travel_date).toLocaleDateString()} - {new Date(pkg.return_date).toLocaleDateString()}</span>
              <span className="text-ink/65">⏱️ {pkg.duration_days}D/{pkg.duration_nights}N</span>
              <span className="text-ink/65">📍 {pkg.location}</span>
            </div>

            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-2xl font-bold mb-4">Description</h2>
              <p className="text-ink/80 whitespace-pre-line">{pkg.description}</p>
            </div>

            {(pkg.hotel_name || pkg.hotel_country || pkg.hotel_star_rating) && (
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg shadow-lg p-6 mb-6 border-2 border-cyan-200">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  🏨 Hotel Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {pkg.hotel_image && (
                    <div className="md:col-span-2">
                      <img src={pkg.hotel_image} alt={pkg.hotel_name || 'Hotel'} className="w-full h-44 sm:h-64 object-cover rounded-lg shadow-md" />
                    </div>
                  )}
                  {pkg.hotel_name && (
                    <div className="bg-white p-4 rounded-lg shadow">
                      <p className="text-sm text-ink/65 mb-1">Hotel Name</p>
                      <p className="text-lg font-semibold text-ink">{pkg.hotel_name}</p>
                    </div>
                  )}
                  {pkg.hotel_star_rating && (
                    <div className="bg-white p-4 rounded-lg shadow">
                      <p className="text-sm text-ink/65 mb-1">Rating</p>
                      <p className="text-lg font-semibold text-yellow-600">
                        {'⭐'.repeat(pkg.hotel_star_rating)} {pkg.hotel_star_rating} Star{pkg.hotel_star_rating > 1 ? 's' : ''}
                      </p>
                    </div>
                  )}
                  {pkg.hotel_country && (
                    <div className="bg-white p-4 rounded-lg shadow md:col-span-2">
                      <p className="text-sm text-ink/65 mb-1">Location</p>
                      <p className="text-lg font-semibold text-ink">📍 {pkg.hotel_country}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {pkg.itinerary && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4">Itinerary</h2>
                <p className="text-ink/80 whitespace-pre-line">{pkg.itinerary}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {pkg.inclusions && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-bold mb-4 text-primary">✓ Inclusions</h2>
                  <p className="text-ink/80 whitespace-pre-line">{pkg.inclusions}</p>
                </div>
              )}
              
              {pkg.exclusions && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-bold mb-4 text-red-600">✗ Exclusions</h2>
                  <p className="text-ink/80 whitespace-pre-line">{pkg.exclusions}</p>
                </div>
              )}
            </div>

            {/* Complimentary Items/Gifts */}
            {pkg.complimentary_items && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg shadow-lg p-6 mb-6 border-2 border-purple-200">
                <h2 className="text-xl font-bold mb-4 text-purple-700 flex items-center gap-2">
                  <span>🎁</span>
                  <span>Complimentary Gifts</span>
                </h2>
                <p className="text-gray-800 whitespace-pre-line leading-relaxed">{pkg.complimentary_items}</p>
              </div>
            )}

            {pkg.images && pkg.images.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-bold mb-4">Gallery</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {pkg.images.map(img => (
                    <img key={img.id} src={img.image} alt={img.caption} className="w-full h-32 sm:h-48 object-cover rounded" />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-5 sm:p-6 lg:sticky lg:top-24">
              <h2 className="text-2xl font-bold mb-6">Book This Package</h2>
              
              <div className="mb-6">
                <h3 className="font-bold mb-3">Room Prices</h3>
                
                {/* Child and Infant Pricing Info */}
                <div className="bg-blue-50 p-3 rounded mb-3 text-sm">
                  <div className="flex justify-between py-1">
                    <span className="text-ink/80">Infant</span>
                    <span className="font-semibold text-gray-800">
                      ${(pkg.room_prices[0]?.price * (pkg.infant_price_percentage / 100)).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-ink/80">Child (No Bed)</span>
                    <span className="font-semibold text-gray-800">
                      ${(pkg.room_prices[0]?.price * (pkg.child_no_bed_price_percentage / 100)).toFixed(2)}
                    </span>
                  </div>
                </div>
                
                {/* Adult Room Prices */}
                {pkg.room_prices.filter(rp => rp.available).map(rp => (
                  <div key={rp.id} className="flex justify-between py-2 border-b">
                    <span>{rp.sharing_type_display}</span>
                    <span className="font-bold">${rp.price}</span>
                  </div>
                ))}
              </div>

              {rooms.map((room, index) => {
                const roomPrice = calculateRoomPrice(room);
                const selectedRoomType = pkg.room_prices.find(rp => rp.sharing_type === room.sharing_type);
                
                return (
                <div key={room.id} className="mb-6 p-4 border rounded-lg bg-cream">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-bold">Room {index + 1}</h3>
                    {rooms.length > 1 && (
                      <button onClick={() => removeRoom(room.id)} className="text-red-500 text-sm hover:text-red-700">Remove</button>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Room Type</label>
                      <select value={room.sharing_type} onChange={(e) => updateRoom(room.id, 'sharing_type', e.target.value)}
                              className="w-full border rounded px-3 py-2">
                        <option value="">Select Type</option>
                        {pkg.room_prices.filter(rp => rp.available).map(rp => (
                          <option key={rp.id} value={rp.sharing_type}>{rp.sharing_type_display} - ${rp.price}/person</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-sm font-medium mb-1">Adults</label>
                        <input 
                          type="number" 
                          min="0" 
                          max={room.sharing_type ? maxAdultsPerType[room.sharing_type] : 4}
                          value={room.num_adults} 
                          onChange={(e) => updateRoom(room.id, 'num_adults', parseInt(e.target.value) || 0)}
                          className="w-full border rounded px-2 py-1" 
                          disabled={!room.sharing_type}
                        />
                        {room.sharing_type && (
                          <span className="text-xs text-ink/50">Max: {maxAdultsPerType[room.sharing_type]}</span>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Child (No Bed)</label>
                        <input type="number" min="0" value={room.num_child_no_bed}
                               onChange={(e) => updateRoom(room.id, 'num_child_no_bed', parseInt(e.target.value) || 0)}
                               className="w-full border rounded px-2 py-1" />
                        {room.sharing_type && (
                          <span className="text-xs text-ink/50">{pkg.child_no_bed_price_percentage}% of adult</span>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Infants</label>
                        <input type="number" min="0" value={room.num_infants}
                               onChange={(e) => updateRoom(room.id, 'num_infants', parseInt(e.target.value) || 0)}
                               className="w-full border rounded px-2 py-1" />
                        {room.sharing_type && (
                          <span className="text-xs text-ink/50">{pkg.infant_price_percentage}% of adult</span>
                        )}
                      </div>
                    </div>

                    {/* Price Breakdown */}
                    {room.sharing_type && (room.num_adults > 0 || room.num_child_no_bed > 0 || room.num_infants > 0) && (
                      <div className="mt-3 p-3 bg-white rounded border">
                        <div className="text-sm space-y-1">
                          {room.num_adults > 0 && (
                            <div className="flex justify-between">
                              <span>{room.num_adults} Adult(s) × ${selectedRoomType.price}</span>
                              <span className="font-semibold">${(selectedRoomType.price * room.num_adults).toFixed(2)}</span>
                            </div>
                          )}
                          {room.num_child_no_bed > 0 && (
                            <div className="flex justify-between text-blue-600">
                              <span>{room.num_child_no_bed} Child(ren) No Bed × ${(selectedRoomType.price * (pkg.child_no_bed_price_percentage / 100)).toFixed(2)}</span>
                              <span className="font-semibold">${(selectedRoomType.price * (pkg.child_no_bed_price_percentage / 100) * room.num_child_no_bed).toFixed(2)}</span>
                            </div>
                          )}
                          {room.num_infants > 0 && (
                            <div className="flex justify-between text-primary">
                              <span>{room.num_infants} Infant(s) × ${(selectedRoomType.price * (pkg.infant_price_percentage / 100)).toFixed(2)}</span>
                              <span className="font-semibold">${(selectedRoomType.price * (pkg.infant_price_percentage / 100) * room.num_infants).toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between pt-2 border-t font-bold text-primary">
                            <span>Room Total:</span>
                            <span>${roomPrice.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )})}


              <button onClick={addRoom} className="w-full border-2 border-dashed border-sand rounded-lg py-2 mb-4 hover:border-primary">
                + Add Another Room
              </button>

              {/* Total Price Summary */}
              {calculateTotalPrice() > 0 && (
                <div className="mb-4 p-4 bg-gradient-to-r from-cream to-sand rounded-lg border-2 border-sand">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm text-ink/65">Total Package Price</div>
                      <div className="text-xs text-ink/50 mt-1">
                        {rooms.reduce((sum, r) => sum + r.num_adults, 0)} Adults, {' '}
                        {rooms.reduce((sum, r) => sum + r.num_child_no_bed, 0)} Child (No Bed), {' '}
                        {rooms.reduce((sum, r) => sum + r.num_infants, 0)} Infants
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-primary">${calculateTotalPrice().toFixed(2)}</div>
                      <div className="text-xs text-ink/65">for {rooms.length} room(s)</div>
                    </div>
                  </div>
                </div>
              )}

              <button onClick={handleNext} className="w-full bg-primary text-white py-3 rounded-lg font-bold hover:bg-secondary">
                Next: Passenger Details
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer navigate={navigate} />
    </div>
  );
};

export default PackageDetailPage;
